import {
  cursorPaging,
  type MessageTemplate,
  type NotificationInboxItem,
  type NotificationOutboxFilter,
  type NotificationOutboxItem,
  type NotificationPreferencesPatch,
} from "@clubcore/api";
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { useScope } from "../members/scope";

/** How many of my notifications are unread; the bell polls it every minute. */
export function useUnreadCount() {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "notifications", "unread"], queryFn: ({ signal }) => api.notifications.unread(signal), staleTime: 10_000, refetchInterval: 15_000, refetchOnWindowFocus: "always" });
}

export function useInbox(unreadOnly: boolean) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "notifications", "inbox", unreadOnly],
    ...cursorPaging<NotificationInboxItem>(({ cursor, signal }) => api.notifications.inbox({ cursor, signal, limit: 50, ...(unreadOnly ? { unreadOnly } : {}) })),
  });
}

function useRefreshInbox() {
  const { queryClient, slug } = useScope();
  return () => queryClient.invalidateQueries({ queryKey: [slug, "notifications"] });
}

/** `ids` undefined marks everything read. */
export function useMarkRead() {
  const { api } = useScope();
  const refresh = useRefreshInbox();
  return useMutation({ mutationFn: (ids: string[] | undefined) => api.notifications.markRead(ids ? { ids } : {}), onSuccess: refresh });
}

export function useMyPreferences() {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "notifications", "preferences"], queryFn: ({ signal }) => api.notifications.preferences(signal) });
}

export function useSavePreferences() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: (input: NotificationPreferencesPatch) => api.notifications.updatePreferences(input),
    onSuccess: (saved) => queryClient.setQueryData([slug, "notifications", "preferences"], saved),
  });
}

export function useOutbox(filter: NotificationOutboxFilter) {
  const { api, slug } = useScope();
  return useInfiniteQuery({
    queryKey: [slug, "notifications", "outbox", filter],
    ...cursorPaging<NotificationOutboxItem>(({ cursor, signal }) => api.notifications.outbox({ cursor, signal, limit: 50, ...filter })),
  });
}

export function useOutboxSummary() {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "notifications", "outbox-summary"], queryFn: ({ signal }) => api.notifications.outboxSummary(signal) });
}

export function useRetry() {
  const { api } = useScope();
  const refresh = useRefreshInbox();
  return useMutation({ mutationFn: (id: string) => api.notifications.retry(id), onSuccess: refresh });
}

export function useCancel() {
  const { api } = useScope();
  const refresh = useRefreshInbox();
  return useMutation({ mutationFn: (id: string) => api.notifications.cancel(id), onSuccess: refresh });
}

export function useTemplates() {
  const { api, slug } = useScope();
  return useQuery({ queryKey: [slug, "notifications", "templates"], queryFn: ({ signal }) => api.notifications.templates(signal) });
}

export type TemplateRef = Pick<MessageTemplate, "topic" | "channel" | "language">;

export function useSaveTemplate() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: ({ topic, channel, language, subject, body }: TemplateRef & { subject: string | null; body: string }) =>
      api.notifications.saveTemplate(topic, channel, language, { subject, body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [slug, "notifications", "templates"] }),
  });
}

export function useResetTemplate() {
  const { api, queryClient, slug } = useScope();
  return useMutation({
    mutationFn: ({ topic, channel, language }: TemplateRef) => api.notifications.resetTemplate(topic, channel, language),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [slug, "notifications", "templates"] }),
  });
}

/** The draft filled in with sample values; refetched as the draft changes (the caller debounces it). */
export function useTemplatePreview(draft: (TemplateRef & { subject: string | null; body: string }) | null) {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "notifications", "template-preview", draft],
    enabled: draft !== null && draft.body.trim() !== "",
    placeholderData: keepPreviousData,
    queryFn: () => (draft ? api.notifications.previewTemplate(draft) : Promise.reject(new Error("no draft"))),
  });
}
